// Storage pro FlowioFileDropzone
import produce from "immer";
import _groupBy from "lodash/groupBy";
import { useCallback, useState } from "react";
import { DropEvent, FileRejection } from "react-dropzone";
import { MAX_FILE_SIZE } from "src/components/molecules/modals/FeedbackModal/GeneralFeedbackModal";
import { getBasenameFromHtml } from "src/utils/global";
import { noop } from "../../../../globalConsts";
import flowioast from "../../../../utils/flowioast";
import { CustomDropzoneOptions } from "./Dropzone";

const areFiledIdentical = (file1: File, file2: File) => {
  const attrs: (keyof File)[] = ["name", "lastModified", "type", "size"];
  return attrs.every((attr) => file1[attr] === file2[attr]);
};

type Options = {
  maxFiles?: number;
};

const defaultOptions: Options = {
  maxFiles: Infinity,
};

export const useFileStorage = (optionsIn: Options = {}) => {
  const options = { ...defaultOptions, ...optionsIn };
  const { maxFiles } = options;

  const [files, setFiles] = useState<File[]>([]);

  const deleteFile = (index: number) => {
    setFiles((old) => old.filter((f, i) => i !== index));
  };

  const addFiles = (newFiles: File[]) => {
    setFiles((old) => {
      const nonDuplicateFiles = produce([] as File[], (draft) => {
        newFiles.forEach((candidate) => {
          const isDuplicate = [...old, ...draft].some((f) =>
            areFiledIdentical(f, candidate)
          );

          if (isDuplicate) {
            const params = { fileName: candidate.name };
            flowioast.info.fromIntlBase("file.duplicate", params);
            return;
          }

          draft.push(candidate);
        });
      });

      const untrimmed = [...old, ...nonDuplicateFiles];
      const trimmed = untrimmed.slice(0, maxFiles);
      const overflowing = untrimmed.slice(maxFiles);

      if (overflowing.length) {
        const names = overflowing.map((f) => f.name).join(", ");
        const max = maxFiles;
        flowioast.info.fromIntlBase("file.rejected.too_many", { names, max });
      }

      return trimmed;
    });
  };

  return {
    files,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    addFiles: useCallback(addFiles, []), // asi je dobrý stabilizovat referenci, bo to dělaj v docs examplech
    deleteFile: useCallback(deleteFile, []), // asi je dobrý stabilizovat referenci, bo to dělaj v docs examplech
  };
};

export const handleLargeFilesError = (
  fileNames: string[],
  maxSize = MAX_FILE_SIZE
) => {
  const names = fileNames.join(", ");
  const kb = maxSize / 1024;
  flowioast.info.fromIntlBase("file.rejected.too_large", { kb, names });
};

export const isTooLarge = (file: File) => file.size > MAX_FILE_SIZE;

// Defaultní reject logika, která se provádí vždy, když explicitně dodaný
// onDropReject nevrátí true
export const useOnReject = (options: CustomDropzoneOptions) => {
  const onReject = (fileRejections: FileRejection[], event: DropEvent) => {
    // Umožňuje explicitně předat reject handler v props.
    // Podle jeho return value se rozhodne, jestli se dál zabývat defaultní logikou
    const { onDropRejected: manualRejector = noop } = options;
    const alreadyHandled = manualRejector(fileRejections, event);
    if (alreadyHandled) return;

    const grouped = _groupBy(fileRejections, (rej) => rej.errors[0].code);

    const wrongExts = grouped["file-invalid-type"];
    const tooLarge = grouped["file-too-large"];

    if (wrongExts) {
      const names = wrongExts.map((rej) => rej.file.name).join(", ");
      flowioast.info.fromIntlBase("file.rejected.wrong_type", { names });
    }

    if (tooLarge) {
      const names = tooLarge.map((rej) => rej.file.name);
      handleLargeFilesError(names, Number(options.maxSize));
    }
  };

  return onReject;
};

export const getFileBlob = (file: File) => URL.createObjectURL(file);
